import GenericFetch from 'framework/genericfetch'
import { serialize } from 'helpers'
import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'
import { of, throwError, EMPTY } from 'rxjs'
import { mergeMap, expand, reduce } from 'rxjs/operators'
import { ERROR_BACKEND_MAPPING } from './errors'

/**
 * Fetching API class.
 */
export default class Fetch extends GenericFetch {
    constructor(config, otherApis = {}) {
        super(config, 'nagra')
        this.registerErrors(ERROR_BACKEND_MAPPING)
    }

    fetch({ url, endpoint, params = {}, ...rest }) {
        const nargraUrl =
            url || this.config.urls.apiUrl + endpoint + serialize(omitBy(params, isNil))
        return super.fetch({ url: nargraUrl, params, ...rest }).pipe(
            mergeMap((result) => {
                // As nagra returns error on HTTP 2xx, implement this workaround for error detection
                const error = this.extract2xxResponseError(result.response)
                if (error) {
                    this.logger.warn('Received HTTP 200 Error', error)
                    return throwError(error)
                }
                return of(result)
            })
        )
    }

    /**
     * Recursively executes an api call until all results have been retrieved
     *
     * @param {function} req The function called to fetch elements from the backend
     * @param {Object} opts
     * @param {number} opts.pageSize The number of elements to be retrieved per fetch
     * @param {number} opts.offset The offset of the first element of next backend call
     * @param {number} opts.responseResource Resources array to be parsed
     *
     * @returns {Array<object>} An array of raw elements
     */
    recursiveFetch(req, opts = {}) {
        const { offset: initialOffset = 0, limit = 50, responseResource } = opts

        let offset = initialOffset
        return req(null, limit, offset).pipe(
            expand(({ response = {}, headers }) => {
                const { totalRecords, total_records } = response

                const records = totalRecords || total_records
                offset += limit

                const continueParsing = records && offset < records
                return continueParsing
                    ? req(response, limit, offset)
                    : of(response).pipe(mergeMap(() => EMPTY))
            }),
            reduce((acc, { response }) => {
                const result = responseResource ? response[responseResource] : response
                result && acc.push(...result)
                return acc
            }, [])
        )
    }

    /**
     * Due to nagra limits backend can return error in response body but OK (200) in header
     * So we should check request body and extract an error
     * @param {Object} response - response object from API
     * @returns {VoltError|undefined} returns {@link VoltError} in case if body contains an error
     *  otherway returns undefined
     */
    extract2xxResponseError(response) {
        if (!response || !response.resultCode) return
        return this._parseError(response)
    }

    /**
     * Error extraction. Inherited from fetch
     * @param {Object} response
     * @returns
     */
    extractError(response = {}) {
        if (Array.isArray(response) && response.length > 0) {
            /**
             * Some API like the recordings one are returning an array rather than object
             */
            response = response[0]
        }
        if (response.code) {
            return {
                error: response.code,
                errorCode: response.errorCode,
                description: response.message,
            }
        }

        if (response.message) {
            return {
                error: response.message,
                errorCode: 'unknown',
                description: 'unknown',
            }
        }

        if (response.requestId || response.resultCode) {
            return {
                error: response.resultCode,
                errorCode: response.result,
                description: response.localeMessage,
            }
        }

        return { error: 'unknown', errorCode: 'unknown', description: 'unknown' }
    }

    // ---------------------------- TOOLBOX ----------------------------
    /**
     * Build filter
     * @doc https://docs.nagra.com/doc/otv/otvdoc2051/apis/metadata-server-mds-api-documentation/api-syntax-101
     * @param {String} key Key
     * @param {String} value Value
     * @returns Nagra SUB filter
     */
    _appendFilter(key = '', value = '') {
        return `"${key}": "${value}"`
    }

    /**
     * Build filter list
     * @doc https://docs.nagra.com/doc/otv/otvdoc2051/apis/metadata-server-mds-api-documentation/api-syntax-101
     * @param {String} fitlerType fitlerType
     * @param {String} key Key
     * @param {Array<String>|<String>} values Values
     * @returns {String} Nagra SUB filter
     */
    _appendFilterList(
        fitlerType = '$or',
        key = '',
        values = '',
        formatCb = (key, val) => `{"${key}": "${val}"}`
    ) {
        const _values = Array.isArray(values) ? values : [values]
        const result = (_values || []).map((val) => formatCb(key, val)).join(',')
        return `"${fitlerType}": [${result}]`
    }
}
